import {
    findIndex,
    find,
    update as defaultUpdate,
    remove as defaultRemove,
    useState as useDefaultState,
    useGetters as useDefaultGetters,
    useMutators as useDefaultMutators,
    useActions as useDefaultActions,
} from "@/js/Store/_default";
import axios from "axios";
import {MAP_DEFAULT_ZOOM_LEVEL} from "@/js/constants.js";

const defaultMapSettings = {
    id: null,
    center: null,
    zoom: MAP_DEFAULT_ZOOM_LEVEL,
    controls: [],
    ready: false,
    units: {},
    keys: {},
    maps: [],
    showHtmlGeolocation: false,
};

export function useState() {
    return {
        ...useDefaultState(),
        name: null,
        layerGroup: null,
        layer: null,
        popupComponent: null,
        highlight: null,
        layerClass: null,
        makeLayer: null,
        sidebarList: [],
        mapFilter: {},
        mapFilterIsSet: 1,
        mapSettings: defaultMapSettings,
    }
}

export function useGetters() {
    return {
        ...useDefaultGetters(),
        layerGroup: state => state.layerGroup,
        layer: state => state.layer,
        getLayer: (state) => (id) => state.layerGroup.findLayer(id),
        selectedLayer: (state)  => state.layerGroup.findLayer(state.selected),
        map: (state) => (state.layerGroup?._map || state.layerGroup?.mapRef),
        mapComponent: (state) => (state.layerGroup?._map || state.layerGroup?.mapRef)?.component,
        popupComponent: (state) => state.popupComponent,
        sidebarList: state => state.sidebarList,
        mapFilter: state => state.mapFilter,
        mapFilterIsSet: state => state.mapFilterIsSet,
        settings: state => state.mapSettings,
    }
}
export function useMutators() {
    return {
        ...useDefaultMutators({
            update, remove
        }),

        layerGroup(state, instance) {
            state.layerGroup = instance;
        },

        layer(state, leafletClass) {
            state.layer = leafletClass;
        },

        mapFilterIsSet(state, status) {
            state.mapFilterIsSet = !!status ? 1 : 0;
        },

        mapSettings(state, settings) {
            state.mapSettings = Object.assign(state.mapSettings, settings);
        },

        mapFilter(state, filter) {
            Object.assign(state.mapFilter, filter);
        },

        setMapFilter(state, filter) {
            state.mapFilter = filter;
        },

        sidebarList(state, list) {
            state.sidebarList = list;
        },

        prependToSidebarList(state, item) {
            state.sidebarList.unshift(item);
        },

        addToSidebarList(state, id) {
            state.sidebarList.push(id);
        },

        clearSidebarList(state, id) {
            state.sidebarList = [];
        },

        updateOrAddOnlyInList(state, item) {
            const index = findIndex(state, item.id);
            if (index !== -1) return defaultUpdate(state, index, item);
            state.list.push(item)
        },

        addToMap(state, id) {
            const object = find(state, id);
            if (!object) return;
            state.layerGroup.add([object], state.layer);
        },

        removeFromMap(state, id) {
            const object = find(state, id);
            if (!object) return;
            removeLayer(state, object);
        },

        clearList(state) {
            state.list = [];
            if (!state.layerGroup || typeof state.layerGroup?.clearLayers !== 'function') return;
            state.layerGroup.clearLayers();
        },

        clearLayerGroup(state) {
            if (typeof state.layerGroup.clearLayers !== 'function') return;
            state.layerGroup.clearLayers();
        },

        addLayerGroup(state) {
            addLayerGroup(state.layerGroup);
        },

        removeLayerGroup(state) {
            removeLayerGroup(state.layerGroup);
        },

        toggleLayerGroup(state) {
            state.layerGroup.toggle();
        }
    }
}

export function useActions() {
    return {
        ...useDefaultActions(),

        loadMapObjectsRecursively(store, { name, params, add }) {
            const hash = store.state.hash;
            return new Promise((resolve, reject) => {
                loadMapObjectsRecursively(
                    store,
                    hash ? `/sharing/${hash}/objects` : `/payload/paginated/map/${name}`,
                    params,
                    typeof add === 'undefined' ? true : add,
                    resolve,
                    reject
                );
            });
        },

        loadMapObjects(store, { name, params, add }) {
            const hash = store.state.hash;
            return loadMapObjects(
                store,
                hash ? `/sharing/${hash}/objects` : `/payload/paginated/map/${name}`,
                params,
                typeof add === 'undefined' ? true : add
            );
        },

        updateActiveStatus({ state, commit }, { ids, active}) {
            ids.forEach(id => {
                commit('updateInList', { id, active });
                commit(active ? 'addToMap' : 'removeFromMap', id);
            });
        },
    };
}

function loadMapObjectsRecursively(store, url, params, add, resolve, reject) {
    loadMapObjects(
        store,
        url,
        params,
        add
    ).then((response) => {
        url = response.data.data.next_page_url;
        if (url) {
            loadMapObjectsRecursively(
                store,
                url,
                params,
                add,
                resolve
            );
            return;
        }

        if (store.state.layerGroup instanceof L.Cluster) {
            store.state.layerGroup.refresh();
        }

        resolve(response);
    }).catch(reject);
}

function loadMapObjects(store, url, params, add, resolve) {
    return new Promise((resolve, reject) => {
        axios.get(url, { params }).then((response) => {
            const data = response?.data?.data?.data;
            if (Array.isArray(data)) {
                response.data.data.data.forEach((object) => {
                    const index = findIndex(store.state, object.id);
                    if (index !== -1) {
                        store.commit('updateOrAddOnlyInList', object);
                    } else {
                        store.commit('addToList', object);
                    }
                });

                if (add && typeof store.state.layerGroup.add === 'function') {
                    store.state.layerGroup.add(data, store.state.layer);
                }
            }
            resolve(response);
        }).catch(reject);
    })
}

export function update(state, index, data) {
    defaultUpdate(state, index, data);
}

export function remove(state, index) {
    removeLayer(state, state.list[index]);
    defaultRemove(state, index);
}

function removeLayer(state, object) {
    if (!state.layerGroup) return;
    const layer = state.layerGroup.findLayer(object.id);
    if (!layer) return;
    state.layerGroup.removeLayer(layer);
}

export function removeLayerGroup(layerGroup) {
    if (!layerGroup || !layerGroup._map) return;
    const mapRef = layerGroup._map;
    layerGroup._map.removeLayer(layerGroup);
    layerGroup.mapRef = mapRef;
}

export function addLayerGroup(layerGroup) {
    if(!layerGroup || !layerGroup.mapRef || layerGroup._map) return;
    layerGroup.addTo(layerGroup.mapRef);
    delete layerGroup.mapRef;
}

export { defaultMapSettings };